import { spacing, Text } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { type APIAlert, APIAlertSeverityEnum } from '../../gen/openapi';
import { useAlerts } from '../../hooks/useAlerts';
import type { FollowUpQuestion } from '../ai-tools/FollowUpQuestions/FollowUpQuestions';
import { ThinkingLoader } from '../chat-messages/ThinkingLoader';
import { GuardianContext } from '../GuardianProvider';
import { SupportPromptGroup } from '../message-composer/SupportPromptGroup';
import { AlertAnalysis } from './AlertAnalysis';

const STATUS_MESSAGE_OK = 'No Guardian alerts currently. How can I help you today?';
const STATUS_MESSAGE_LOADING = 'Loading alerts...';
const STATUS_MESSAGE_ERROR = 'Error loading alerts.';
const HIGH_SEVERITIES = [APIAlertSeverityEnum.High, APIAlertSeverityEnum.Critical] as string[];

const genAlertStatusMessage = (alerts: APIAlert[]) => {
  const alertCount = alerts.length;
  const criticalAlerts = alerts.filter((alert) => alert.severity === APIAlertSeverityEnum.Critical).length;
  const criticalAlertsMessage =
    criticalAlerts > 0 ? ` - ${criticalAlerts} critical${criticalAlerts > 1 ? 's' : ''}` : '';
  return `${alertCount} active alert${alertCount > 1 ? 's' : ''}${criticalAlertsMessage}`;
};

const FOLLOW_UP_NO_ALERT: string[] = ["What's the health of my deployment?", 'How Guardian can help me'];
const FOLLOW_UP_WITH_ALERT: string[] = ["What's the health of my deployment?", 'Check Guardian alerts on my platform'];
const genFollowUpQuestions = (titles: string[]): FollowUpQuestion[] => {
  return titles.map((title) => ({
    id: uuidv4(),
    title,
    prompt: `/follow-up ${title}`,
  }));
};

export const StatusDotOk = styled.div<{ size?: number }>`
  height: ${(props) => (props.size ? props.size : 32)}px;
  width: ${(props) => (props.size ? props.size : 32)}px;
  background-image: ${(props) => props.theme.buttonPrimary};
  border-radius: 50%;
`;

export const StatusDotLoading = styled(StatusDotOk)`
  background: ${(props) => props.theme.buttonSecondary};
`;

export const StatusDotWarning = styled(StatusDotOk)`
  background-image: linear-gradient(130deg, #fdc064ff 0%, #f57c00 60%);
`;

export const StatusDotError = styled(StatusDotOk)`
  background-image: linear-gradient(130deg, #f0675eff 0%, #d32f2f 60%);
`;

type Status = 'critical' | 'warning' | 'healthy';

const AlertBox = styled.div<{ status: Status }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.r16};
  border: 1px solid
    ${(props) =>
      props.status === 'critical'
        ? props.theme.statusCritical
        : props.status === 'warning'
          ? props.theme.statusWarning
          : 'transparent'};
  border-radius: ${spacing.f8};
  padding: ${spacing.r16};
  color: ${(props) => props.theme.textPrimary};
  background-color: ${(props) => props.theme.backgroundLevel2};
`;

const AlertBoxTitle = styled(Text)<{ isAlerting: boolean }>`
  font-size: 16px;
  padding: ${spacing.r2} ${spacing.r8};
`;

export const Alerts: React.FC<{ onSubmit: (message: string) => void; selectedInstanceIds: string[] }> = ({
  onSubmit,
  selectedInstanceIds,
}) => {
  const alertQuery = useAlerts();
  const filteredAlerts = useMemo(
    () => (alertQuery.data?.alerts ?? []).filter((alert) => selectedInstanceIds.includes(alert.instanceId)),
    [alertQuery.data?.alerts, selectedInstanceIds],
  );
  const isAlerting = filteredAlerts.length > 0;
  let StatusDot, statusString;
  let status: Status = 'healthy';

  switch (true) {
    case alertQuery.isLoading:
      StatusDot = StatusDotLoading;
      statusString = STATUS_MESSAGE_LOADING;
      break;
    case alertQuery.isError:
      StatusDot = StatusDotError;
      statusString = STATUS_MESSAGE_ERROR;
      status = 'critical';
      break;
    case isAlerting: {
      const isHighSeverity = filteredAlerts.some((alert) => HIGH_SEVERITIES.includes(alert.severity));
      StatusDot = isHighSeverity ? StatusDotError : StatusDotWarning;
      status = isHighSeverity ? 'critical' : 'warning';
      statusString = genAlertStatusMessage(filteredAlerts);
      break;
    }
    default:
      StatusDot = StatusDotOk;
      statusString = STATUS_MESSAGE_OK;
  }

  const { setFollowUpQuestions } = useContext(GuardianContext);

  useEffect(() => {
    const FOLLOW_UP_TITLES = isAlerting ? FOLLOW_UP_WITH_ALERT : FOLLOW_UP_NO_ALERT;
    setFollowUpQuestions(genFollowUpQuestions(FOLLOW_UP_TITLES));
    return () => setFollowUpQuestions([]);
  }, [isAlerting, setFollowUpQuestions]);

  return (
    <AlertBox status={status}>
      <Box display="flex" gap={spacing.r8} alignItems="center">
        <StatusDot />
        <AlertBoxTitle isAlerting={isAlerting}>
          {alertQuery.isLoading ? <ThinkingLoader params={{ message: STATUS_MESSAGE_LOADING }} /> : statusString}
        </AlertBoxTitle>
      </Box>

      {isAlerting && <AlertAnalysis alerts={filteredAlerts} />}

      {!alertQuery.isLoading && <SupportPromptGroup onSubmit={onSubmit} />}
    </AlertBox>
  );
};
